import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, AdminRole, OrgExtraction, OrgProject } from '../api';
import {
  ConfirmRunModal,
  ExtractionRunsView,
  RUN_STATUS_LABEL,
  describeCron,
  getErrorMessage,
  runStatusColor,
  summarizeRunCounts,
  type ExtractionRunsSource,
} from '../../../shared/src';

interface ExtractionsTabProps {
  orgId: string;
  orgSlug: string;
  adminRole: AdminRole;
}

/**
 * Superadmin console → organization → Extractions.
 *
 * Extractions are defined and deployed from the data-extraction repository
 * (one YAML per extraction); this tab shows what is deployed, read-only, as a
 * table where the status, the last run and the mapped projects stand out. A
 * row opens a modal with the full configuration and the project mapping, the
 * only thing an admin edits here. Runs are not tied to projects: "Run now"
 * and "View runs" work whether or not the extraction is mapped; the mapping
 * only decides which customer users see it.
 */
export function ExtractionsTab({ orgId, orgSlug, adminRole }: ExtractionsTabProps) {
  const canEdit = adminRole !== 'admin_viewer';
  const [extractions, setExtractions] = useState<OrgExtraction[]>([]);
  const [orphanedKeys, setOrphanedKeys] = useState<string[]>([]);
  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [ledgerConfigured, setLedgerConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [viewingRuns, setViewingRuns] = useState<OrgExtraction | null>(null);
  const [details, setDetails] = useState<OrgExtraction | null>(null);
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [confirmRun, setConfirmRun] = useState<OrgExtraction | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ext, proj] = await Promise.all([api.listOrgExtractions(orgId), api.listOrgProjects(orgId)]);
      setExtractions(ext.extractions);
      setOrphanedKeys(ext.orphaned_keys);
      setLedgerConfigured(ext.ledger_configured);
      setProjects(proj.projects);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const runsSource = useMemo<ExtractionRunsSource | null>(() => {
    if (!viewingRuns) return null;
    const key = viewingRuns.key;
    return {
      listRuns: () => api.listOrgExtractionRuns(orgId, key),
      getRun: (runId: string, cursor?: string) => api.getOrgExtractionRun(orgId, key, runId, cursor),
    };
  }, [orgId, viewingRuns]);

  const runNow = async (extraction: OrgExtraction) => {
    setStartingKey(extraction.key);
    setError(null);
    setNotice(null);
    try {
      const res = await api.triggerOrgExtractionRun(orgId, extraction.key);
      // Straight to the new run of that extraction.
      setOpenRunId(res.run.run_id);
      setViewingRuns(extraction);
    } finally {
      setStartingKey(null);
    }
  };

  if (viewingRuns && runsSource) {
    return (
      <div className="tab-content">
        <ExtractionRunsView
          extraction={viewingRuns}
          source={runsSource}
          initialRunId={openRunId}
          onBack={() => {
            setOpenRunId(null);
            setViewingRuns(null);
            load();
          }}
          backLabel="All extractions"
          onRunNow={canEdit ? async () => (await api.triggerOrgExtractionRun(orgId, viewingRuns.key)).run : undefined}
        />
      </div>
    );
  }

  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? id;

  return (
    <div className="tab-content">
      <div className="section-title-row">
        <h3 className="section-title">Automated extractions</h3>
        <button type="button" className="btn-secondary btn-small" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>
      <p className="project-selection-hint">
        Defined in <code>data-extraction</code> as <code>extractions/{orgSlug}/&lt;key&gt;.yaml</code>. Click a row for the configuration; map projects to make runs visible to customer users.
      </p>

      {error && <div className="error-message">{error}</div>}
      {notice && <div className="success-message">{notice}</div>}
      {!ledgerConfigured && !error && <div className="extraction-runs-notice">The extraction ledger is not wired in this environment yet.</div>}
      {orphanedKeys.length > 0 && (
        <div className="extraction-runs-notice">
          Mapped but no longer deployed: {orphanedKeys.map((k) => <code key={k}>{k}</code>)}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading extractions...</div>
      ) : (
        <table className="data-table extractions-table">
          <thead>
            <tr>
              <th>Extraction</th>
              <th>Status</th>
              <th>Last run</th>
              <th>Schedule</th>
              <th>Projects</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {extractions.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No extraction deployed for this organization.
                </td>
              </tr>
            ) : (
              extractions.map((e) => (
                <tr key={e.key} className="extractions-row" onClick={() => setDetails(e)}>
                  <td>
                    <div className="extractions-name">
                      <strong>{e.name}</strong>
                      <span className="status-badge gray extraction-type-badge">{e.source_type}</span>
                    </div>
                    <div className="extraction-muted">
                      <code>{e.key}</code>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${e.enabled ? 'green' : 'yellow'}`}>{e.enabled ? 'enabled' : 'paused'}</span>
                  </td>
                  <td>
                    {e.latest_run ? (
                      <div className="extractions-lastrun">
                        <span className={`status-badge ${runStatusColor(e.latest_run.status)}`}>{RUN_STATUS_LABEL[e.latest_run.status]}</span>
                        <span className="extraction-muted" title={summarizeRunCounts(e.latest_run.counts)}>
                          {new Date(e.latest_run.started_at).toLocaleString()}
                        </span>
                      </div>
                    ) : (
                      <span className="extraction-muted">Never ran</span>
                    )}
                  </td>
                  <td className="extraction-muted">{describeCron(e.schedule.run, e.schedule.timezone)}</td>
                  <td>
                    {e.project_ids.length === 0 ? (
                      <span className="status-badge gray">No project</span>
                    ) : (
                      <div className="extractions-projects">
                        {e.project_ids.map((id) => (
                          <span key={id} className="status-badge green">{projectName(id)}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="actions-cell" onClick={(ev) => ev.stopPropagation()}>
                    <div className="extraction-runs-actions">
                      {canEdit && (
                        <button type="button" className="btn-primary btn-small" onClick={() => setConfirmRun(e)} disabled={startingKey === e.key}>
                          {startingKey === e.key ? 'Starting...' : 'Run now'}
                        </button>
                      )}
                      <button type="button" className="btn-secondary btn-small" onClick={() => setViewingRuns(e)}>
                        View runs
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {confirmRun && (
        <ConfirmRunModal extractionName={confirmRun.name} onClose={() => setConfirmRun(null)} onConfirm={() => runNow(confirmRun)} />
      )}

      {details && (
        <ExtractionDetailsModal
          orgId={orgId}
          extraction={details}
          projects={projects}
          canEdit={canEdit}
          onClose={() => setDetails(null)}
          onChanged={(updated) => {
            setExtractions((list) => list.map((x) => (x.key === updated.key ? { ...x, ...updated } : x)));
            setDetails((d) => (d ? { ...d, ...updated } : d));
          }}
        />
      )}
    </div>
  );
}

interface ExtractionDetailsModalProps {
  orgId: string;
  extraction: OrgExtraction;
  projects: OrgProject[];
  canEdit: boolean;
  onClose: () => void;
  onChanged: (updated: Partial<OrgExtraction> & { key: string }) => void;
}

function ExtractionDetailsModal({ orgId, extraction, projects, canEdit, onClose, onChanged }: ExtractionDetailsModalProps) {
  const [selected, setSelected] = useState<string[]>(extraction.project_ids);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = selected.length !== extraction.project_ids.length || selected.some((id) => !extraction.project_ids.includes(id));
  const chips = (values: string[]) => values.map((v) => <code key={v} className="extraction-chip">{v}</code>);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await api.setOrgExtractionProjects(orgId, extraction.key, selected);
      onChanged({ key: extraction.key, project_ids: res.extraction.project_ids });
      setSaved(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="extraction-card-header">
          <div>
            <h2>
              {extraction.name}
              <span className="status-badge gray extraction-type-badge">{extraction.source_type}</span>
              <span className={`status-badge ${extraction.enabled ? 'green' : 'yellow'} extraction-type-badge`}>
                {extraction.enabled ? 'enabled' : 'paused'}
              </span>
            </h2>
            {extraction.description && <p className="extraction-card-description">{extraction.description}</p>}
          </div>
        </div>

        <dl className="extraction-grid">
          <dt>Key</dt>
          <dd>{chips([extraction.key])}</dd>

          <dt>Schedule</dt>
          <dd>
            {describeCron(extraction.schedule.run, extraction.schedule.timezone)}
            {!extraction.enabled && <span className="extraction-muted"> (paused: the schedule is disabled)</span>}
          </dd>

          <dt>Deadline</dt>
          <dd>
            {describeCron(extraction.schedule.deadline, extraction.schedule.timezone)}
            <span className="extraction-muted"> · alert if nothing arrived in the previous {extraction.schedule.deadline_lookback_hours} h</span>
          </dd>

          <dt>Source</dt>
          <dd>
            {chips([`${extraction.host}${extraction.port && extraction.port !== 22 ? `:${extraction.port}` : ''}`])}
            {chips(extraction.remote_paths)}
            {extraction.recursive && <span className="extraction-muted">recursive</span>}
          </dd>

          <dt>Destination</dt>
          <dd>{chips([`s3://${extraction.s3_bucket}/${extraction.s3_prefix}`])}</dd>

          <dt>Stability</dt>
          <dd>
            {extraction.stability.strategy === 'mtime_age'
              ? `Files are pulled once unchanged for ${extraction.stability.min_age_minutes} min`
              : extraction.stability.strategy}
            {extraction.stability.exclude_patterns.length > 0 && (
              <span className="extraction-muted"> · ignoring {extraction.stability.exclude_patterns.join(', ')}</span>
            )}
          </dd>

          <dt>Connector</dt>
          <dd>
            {chips([extraction.connector_id])}
            <span className="extraction-muted">egress IPs</span>
            {chips(extraction.static_egress_ips)}
          </dd>

          <dt>Visible in projects</dt>
          <dd>
            {canEdit ? (
              <div className="checkbox-list horizontal">
                {projects.length === 0 && <span className="extraction-unmapped">No project in this organization</span>}
                {projects.map((p) => (
                  <label key={p.id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selected.includes(p.id)}
                      onChange={(e) => setSelected((s) => (e.target.checked ? [...s, p.id] : s.filter((id) => id !== p.id)))}
                    />
                    <span className="checkbox-label">{p.name}</span>
                  </label>
                ))}
              </div>
            ) : extraction.project_ids.length === 0 ? (
              <span className="status-badge gray">No project</span>
            ) : (
              extraction.project_ids.map((id) => (
                <span key={id} className="status-badge green">{projects.find((p) => p.id === id)?.name ?? id}</span>
              ))
            )}
          </dd>
        </dl>
        <p className="extraction-muted">Mapping only decides which customer users see the runs. Runs themselves are not tied to a project.</p>

        {error && <div className="error-message">{error}</div>}
        {saved && !dirty && <div className="success-message">Project mapping saved.</div>}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Close
          </button>
          {canEdit && (
            <button type="button" className="btn-primary" onClick={save} disabled={saving || !dirty}>
              {saving ? 'Saving...' : 'Save mapping'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
